import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  BadgeCheck,
  Camera,
  CheckCircle2,
  PenLine,
  Loader2,
  Pencil,
  ScanLine,
  Sparkles,
  Trash2,
  UserRound,
  UserSquare2,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { checkFile, FileDropzone, useObjectUrl, type FileRules } from '@/components/ui/dropzone';
import { Field, Input, Select } from '@/components/ui/input';
import { checksApi, passportsApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { Check, PassportExtraction } from '@/lib/api/types';
import {
  applicantToForm,
  arabicFollowAction,
  findTitle,
  SEX_LABEL,
  titledName,
  titledNameAr,
  TITLES,
  toApplicantPatch,
  validateApplicant,
  withSex,
  withTitle,
  type ApplicantErrors,
  type ApplicantForm,
  type ArabicPair,
} from '@/lib/applicant';
import { describeScanFailure, readScanInfo, type ScanInfo } from '@/lib/scanFailure';
import { cn } from '@/lib/utils';

export const PHOTO_RULES: FileRules = {
  accept: ['image/jpeg', 'image/png', 'image/webp'],
  maxBytes: 5 * 1024 * 1024,
  typeMessage: 'Use a JPEG, PNG or WEBP image.',
};

export const SCAN_RULES: FileRules = {
  accept: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
  maxBytes: 10 * 1024 * 1024,
  typeMessage: 'Use a JPEG, PNG, WEBP or PDF file.',
};

/** Fetch a Bearer-protected image (photo_url) as an object URL. */
export function useAuthedImage(url: string | null | undefined, version: string | number = 0) {
  const q = useQuery({
    queryKey: ['authed-image', url, version],
    queryFn: () => checksApi.file(url!),
    enabled: !!url,
    staleTime: Infinity,
  });
  const objectUrl = useObjectUrl(q.data ?? null);
  return { url: url ? objectUrl : null, loading: !!url && q.isPending };
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

/** Highlight for a value the passport scan couldn't read. */
function flaggedClass(flagged: boolean): string {
  return flagged ? 'border-amber-400 ring-4 ring-amber-400/15' : '';
}

function ArabicInput({
  id,
  value,
  onChange,
  source,
  error,
  flagged,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  /** Latin text to suggest from. */
  source: string;
  error?: string;
  flagged?: boolean;
}) {
  const suggest = useMutation({
    mutationFn: () => passportsApi.transliterate(source.trim()),
    onSuccess: (r) => {
      if (!r.arabic) {
        toast.error('No suggestion', { description: r.warnings[0] ?? 'Type the Arabic spelling by hand.' });
        return;
      }
      const previous = value;
      onChange(r.arabic);
      if (previous && previous !== r.arabic) {
        // Never lose a hand-corrected spelling without a way back.
        toast('Arabic spelling replaced', { action: { label: 'Undo', onClick: () => onChange(previous) } });
      }
      if (r.warnings.length) toast.warning(r.warnings[0]);
    },
    onError: (e) => toast.error(userMessage(e)),
  });
  return (
    <div className="flex gap-2">
      <Input
        id={id}
        dir="rtl"
        lang="ar"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        className={cn('font-arabic text-lg', flaggedClass(!!flagged))}
      />
      <Button
        type="button"
        variant="outline"
        className="shrink-0"
        onClick={() => suggest.mutate()}
        loading={suggest.isPending}
        disabled={!source.trim()}
        title="Suggest an Arabic spelling from the English"
      >
        {!suggest.isPending && <Sparkles />} <span className="hidden sm:inline">Suggest</span>
      </Button>
    </div>
  );
}

const MISSING_HINT = "Couldn't read this from the scan. Please fill it in.";
const OTHER_TITLE = '__other';
const RESPELL_DELAY_MS = 700;

/**
 * Keeps an Arabic field in step with its English one: shortly after the
 * English is edited, its Arabic spelling (AI first, else letter rules)
 * replaces the old one (a wrong English name gives a wrong Arabic one, so
 * fixing the English fixes both). Returns true while a spelling is fetched.
 */
function useArabicFollowsEnglish(
  value: ApplicantForm,
  onChange: (v: ApplicantForm) => void,
  pair: ArabicPair,
  enabled = true,
): boolean {
  const [en, ar] = pair;
  const [busy, setBusy] = React.useState(false);
  const latest = React.useRef({ value, onChange });
  latest.current = { value, onChange };
  const previous = React.useRef(value);

  React.useEffect(() => {
    const action = arabicFollowAction(previous.current, value, ...pair);
    previous.current = value;
    if (!enabled) return;
    if (action === 'clear') {
      onChange({ ...value, [ar]: '' });
      return;
    }
    if (action !== 'respell') return;
    const english = value[en].trim();
    let cancelled = false;
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await passportsApi.transliterate(english);
        const now = latest.current.value;
        // Ignore an answer for English that has since changed again
        if (!cancelled && r.arabic && now[en].trim() === english) {
          latest.current.onChange({ ...now, [ar]: r.arabic });
        }
      } catch {
        /* keep the current Arabic; the Suggest button is still there */
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, RESPELL_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setBusy(false);
    };
    // Only the English field drives this; other edits don't respell
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value[en], enabled]);

  return busy;
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/** The AI's review of an Arabic spelling: checked, a suggested fix, or unavailable. */
function ArabicAiCheck({ english, arabic, onUse }: { english: string; arabic: string; onUse: (v: string) => void }) {
  const en = english.trim().toUpperCase();
  const ar = arabic.trim();
  const settledEn = useDebounced(en, 900);
  const settledAr = useDebounced(ar, 900);
  const settled = settledEn === en && settledAr === ar && en.length >= 2 && !!ar;
  const q = useQuery({
    queryKey: ['arabic-check', settledEn, settledAr],
    queryFn: () => passportsApi.checkArabic(settledEn, settledAr),
    enabled: settled,
    staleTime: Infinity,
    retry: false,
  });
  if (!en || !ar) return null;

  const line = 'mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs';
  if (!settled || q.isFetching) {
    return (
      <p className={cn(line, 'text-muted-foreground')}>
        <Loader2 className="size-3.5 animate-spin" /> AI is checking the Arabic spelling…
      </p>
    );
  }
  if (q.isError || !q.data?.checked) {
    return <p className={cn(line, 'text-muted-foreground')}>AI check unavailable right now. Compare the Arabic with the English yourself.</p>;
  }
  if (q.data.ok) {
    return (
      <p className={cn(line, 'font-medium text-emerald-700 dark:text-emerald-400')}>
        <BadgeCheck className="size-3.5" /> Checked by AI: the spelling matches the English.
      </p>
    );
  }
  const suggestion = q.data.suggestion;
  if (!suggestion) {
    return (
      <p className={cn(line, 'font-medium text-amber-700 dark:text-amber-400')}>
        <AlertTriangle className="size-3.5" /> The AI thinks this doesn't spell the English name. Check every word.
      </p>
    );
  }
  return (
    <div className={cn(line, 'rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-2.5 py-1.5 text-amber-800 dark:text-amber-300')}>
      <span className="font-medium">AI suggests:</span>
      <span dir="rtl" lang="ar" className="font-arabic text-base">{suggestion}</span>
      <Button type="button" size="sm" variant="outline" className="ml-auto h-7" onClick={() => onUse(suggestion)}>
        <PenLine /> Use this
      </Button>
    </div>
  );
}

export function ApplicantFields({
  value,
  onChange,
  errors = {},
  flagged = [],
  assist = false,
}: {
  value: ApplicantForm;
  onChange: (v: ApplicantForm) => void;
  errors?: ApplicantErrors;
  flagged?: string[];
  /** Entering by hand: the Arabic name follows the English too, and the AI checks both Arabic fields. */
  assist?: boolean;
}) {
  const set = <K extends keyof ApplicantForm>(k: K, v: ApplicantForm[K]) => onChange({ ...value, [k]: v });
  const isFlagged = (k: keyof ApplicantForm) => flagged.includes(k) && !value[k];
  const hint = (k: keyof ApplicantForm, text?: string) => (isFlagged(k) ? MISSING_HINT : text);
  const respelling = useArabicFollowsEnglish(value, onChange, ['place_of_birth', 'place_of_birth_ar']);
  const respellingName = useArabicFollowsEnglish(value, onChange, ['full_name', 'full_name_ar'], assist);
  const [customTitle, setCustomTitle] = React.useState(false);
  const preset = findTitle(value.title);
  const showCustomTitle = customTitle || (!!value.title.trim() && !preset);
  const titleSelect = showCustomTitle ? OTHER_TITLE : (preset?.en ?? '');
  const sample = value.full_name.trim() || 'FULL NAME';
  const sampleAr = value.full_name_ar.trim() || 'الاسم';

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Full name (English)" htmlFor="a-name" error={errors.full_name} hint={hint('full_name', 'As printed on the passport.')} className="sm:col-span-2">
        <Input
          id="a-name"
          icon={<UserRound />}
          className={cn('uppercase', flaggedClass(isFlagged('full_name')))}
          value={value.full_name}
          onChange={(e) => set('full_name', e.target.value)}
          aria-invalid={!!errors.full_name}
        />
      </Field>
      <Field
        label="Full name (Arabic)"
        htmlFor="a-name-ar"
        error={errors.full_name_ar}
        hint={
          respellingName
            ? 'Writing the Arabic from the English…'
            : hint('full_name_ar', assist ? 'Written automatically from the English, then checked by AI.' : 'A phonetic suggestion. Check it and correct it.')
        }
        className="sm:col-span-2"
      >
        <div>
          <ArabicInput id="a-name-ar" value={value.full_name_ar} onChange={(v) => set('full_name_ar', v)} source={value.full_name} error={errors.full_name_ar} flagged={isFlagged('full_name_ar')} />
          {assist && !respellingName && <ArabicAiCheck english={value.full_name} arabic={value.full_name_ar} onUse={(v) => set('full_name_ar', v)} />}
        </div>
      </Field>
      <Field
        label="Title"
        htmlFor="a-title"
        error={errors.title}
        hint={`On the report: ${titledName(sample, value.title)}`}
      >
        <div className="flex gap-2">
          <Select
            id="a-title"
            className={showCustomTitle ? 'w-32 shrink-0' : undefined}
            value={titleSelect}
            onChange={(e) => {
              const v = e.target.value;
              setCustomTitle(v === OTHER_TITLE);
              onChange(withTitle(value, v === OTHER_TITLE ? '' : v));
            }}
          >
            <option value="">—</option>
            {TITLES.map((t) => (
              <option key={t.en} value={t.en}>{t.en}</option>
            ))}
            <option value={OTHER_TITLE}>Other…</option>
          </Select>
          {showCustomTitle && (
            <Input
              aria-label="Custom title"
              placeholder="e.g. Rev."
              maxLength={32}
              value={value.title}
              onChange={(e) => onChange(withTitle(value, e.target.value))}
              aria-invalid={!!errors.title}
            />
          )}
        </div>
      </Field>
      <Field
        label="Title (Arabic)"
        htmlFor="a-title-ar"
        error={errors.title_ar}
        hint={<span dir="rtl" lang="ar" className="font-arabic">{titledNameAr(sampleAr, value.title_ar)}</span>}
      >
        <Input
          id="a-title-ar"
          dir="rtl"
          lang="ar"
          maxLength={32}
          className="font-arabic text-lg"
          value={value.title_ar}
          onChange={(e) => set('title_ar', e.target.value)}
          aria-invalid={!!errors.title_ar}
        />
      </Field>
      <Field label="Date of birth" htmlFor="a-dob" error={errors.date_of_birth} hint={hint('date_of_birth')}>
        <Input
          id="a-dob"
          type="date"
          className={flaggedClass(isFlagged('date_of_birth'))}
          value={value.date_of_birth}
          onChange={(e) => set('date_of_birth', e.target.value)}
          aria-invalid={!!errors.date_of_birth}
        />
      </Field>
      <div className="grid grid-cols-[1fr_96px] gap-3">
        <Field label="Sex" htmlFor="a-sex" hint={hint('sex')}>
          <Select id="a-sex" className={flaggedClass(isFlagged('sex'))} value={value.sex} onChange={(e) => onChange(withSex(value, e.target.value as ApplicantForm['sex']))}>
            <option value="">—</option>
            {(Object.keys(SEX_LABEL) as (keyof typeof SEX_LABEL)[]).map((s) => (
              <option key={s} value={s}>{SEX_LABEL[s]}</option>
            ))}
          </Select>
        </Field>
        <Field label="Type" htmlFor="a-type" error={errors.passport_type}>
          <Input id="a-type" className="uppercase" maxLength={2} value={value.passport_type} onChange={(e) => set('passport_type', e.target.value)} placeholder="P" />
        </Field>
      </div>
      <Field label="Place of birth (English)" htmlFor="a-pob" error={errors.place_of_birth} hint={hint('place_of_birth')}>
        <Input
          id="a-pob"
          className={cn('uppercase', flaggedClass(isFlagged('place_of_birth')))}
          value={value.place_of_birth}
          onChange={(e) => set('place_of_birth', e.target.value)}
          aria-invalid={!!errors.place_of_birth}
        />
      </Field>
      <Field
        label="Place of birth (Arabic)"
        htmlFor="a-pob-ar"
        error={errors.place_of_birth_ar}
        hint={respelling ? 'Updating the Arabic from the English…' : hint('place_of_birth_ar', 'Follows the English automatically. Check it.')}
      >
        <div>
          <ArabicInput id="a-pob-ar" value={value.place_of_birth_ar} onChange={(v) => set('place_of_birth_ar', v)} source={value.place_of_birth} error={errors.place_of_birth_ar} flagged={isFlagged('place_of_birth_ar')} />
          {assist && !respelling && <ArabicAiCheck english={value.place_of_birth} arabic={value.place_of_birth_ar} onUse={(v) => set('place_of_birth_ar', v)} />}
        </div>
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Photo
// ---------------------------------------------------------------------------

/** 35 × 45 passport-photo frame: shows a picked File or an existing photo URL. */
export function PhotoFrame({ src, loading, className }: { src: string | null; loading?: boolean; className?: string }) {
  return (
    <div className={cn('relative flex aspect-[35/45] w-28 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed bg-secondary/50', src && 'border-solid', className)}>
      {loading ? (
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      ) : src ? (
        <motion.img key={src} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} src={src} alt="Applicant photo" className="size-full object-cover" />
      ) : (
        <UserSquare2 className="size-8 text-muted-foreground/60" />
      )}
    </div>
  );
}

/** Pick a photo before the check exists (uploaded after POST /checks). */
export function PhotoPicker({ file, onFile }: { file: File | null; onFile: (f: File | null) => void }) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const preview = useObjectUrl(file);
  return (
    <div className="flex items-center gap-4">
      <PhotoFrame src={preview} />
      <div className="space-y-2">
        <p className="text-sm font-semibold">Applicant photo <span className="font-normal text-muted-foreground">(optional)</span></p>
        <p className="text-xs text-muted-foreground">Printed on the status report. JPEG, PNG or WEBP, up to 5 MB.</p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
            <Camera /> {file ? 'Change' : 'Choose photo'}
          </Button>
          {file && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onFile(null)}>
              <Trash2 /> Remove
            </Button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_RULES.accept.join(',')}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          const err = checkFile(f, PHOTO_RULES);
          if (err) toast.error(err);
          else onFile(f);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passport scanner
// ---------------------------------------------------------------------------

/** Why the scan failed: a title and the likely causes, with what to do next. */
function ScanFailureNotice({
  error,
  file,
  info,
  onRetry,
  onManual,
}: {
  error: unknown;
  file: File | null;
  info: ScanInfo | null;
  onRetry?: () => void;
  onManual: () => void;
}) {
  const failure = describeScanFailure(error, file, info);
  return (
    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
      <div className="min-w-0 space-y-2">
        <p className="font-semibold">{failure.title}</p>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{failure.reasons.length > 1 ? 'Possible reasons' : 'Reason'}</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
            {failure.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
        {!failure.retrySame && (
          <p className="text-xs text-muted-foreground">
            The same photo usually gives the same result. Choose a clearer one above, or enter the details by hand while looking at this scan.
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {onRetry && (
            <Button type="button" size="sm" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={onManual}>
            Enter by hand
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Upload a passport scan, read it on the server, and hand the result back.
 * onSkip gets the scan that couldn't be read (null if none), to show while typing.
 */
export function PassportScanner({ onResult, onSkip }: { onResult: (x: PassportExtraction) => void; onSkip: (failedScan: File | null) => void }) {
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string>();
  const [info, setInfo] = React.useState<ScanInfo | null>(null);
  const preview = useObjectUrl(file);
  const extract = useMutation({
    mutationFn: (f: File) => passportsApi.extract(f),
    onSuccess: onResult,
  });

  const pick = (f: File | null) => {
    extract.reset();
    setFile(f);
    const err = f ? checkFile(f, SCAN_RULES) : null;
    setFileError(err ?? undefined);
    setInfo(null);
    if (f && !err) {
      readScanInfo(f).then(setInfo);
      extract.mutate(f);
    }
  };
  const failedScan = extract.isError && file && !fileError ? file : null;

  return (
    <div className="space-y-5">
      <AnimatePresence mode="wait">
        {extract.isPending ? (
          <motion.div key="reading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="relative h-44 w-64 overflow-hidden rounded-2xl border bg-secondary shadow-glow">
              {preview ? <img src={preview} alt="" className="size-full object-cover opacity-80" /> : <div className="flex size-full items-center justify-center"><ScanLine className="size-10 text-primary-ink" /></div>}
              <motion.div
                className="absolute inset-x-0 h-12 bg-gradient-to-b from-transparent via-brand-sun/40 to-transparent"
                animate={{ top: ['-20%', '100%'] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut', repeatType: 'reverse' }}
              />
              <div className="absolute inset-x-3 bottom-3 h-8 rounded border-2 border-brand-sun/80 shadow-[0_0_20px_hsl(var(--brand-sun)/0.6)]" />
            </div>
            <div>
              <p className="font-bold">Reading the passport…</p>
              <p className="text-sm text-muted-foreground">Finding the machine-readable lines. This takes a few seconds.</p>
            </div>
          </motion.div>
        ) : (
          <motion.div key="pick" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <FileDropzone
              file={extract.isError ? file : null}
              onFile={pick}
              error={fileError}
              accept={SCAN_RULES.accept}
              icon={<ScanLine />}
              title="Upload a photo or scan of the passport's photo page"
              hint="JPEG, PNG, WEBP or PDF · up to 10 MB · the two lines of <<< at the bottom must be visible"
            />
            {extract.isError && (
              <ScanFailureNotice
                error={extract.error}
                file={failedScan}
                info={info}
                onRetry={failedScan ? () => extract.mutate(failedScan) : undefined}
                onManual={() => onSkip(failedScan)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {!extract.isPending && (
        <div className="flex flex-col items-center gap-1 text-center">
          <Button type="button" variant="link" onClick={() => onSkip(failedScan)}>
            Skip and enter the details by hand
          </Button>
          <p className="text-xs text-muted-foreground">The scan is read and then discarded. It isn't stored.</p>
        </div>
      )}
    </div>
  );
}

/** Summary of an extraction: all good, or what to double-check. */
export function ExtractionNotice({ extraction }: { extraction: PassportExtraction }) {
  const ok = !extraction.needs_review && extraction.check_digits_valid && extraction.warnings.length === 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('flex gap-3 rounded-xl border p-4 text-sm', ok ? 'border-emerald-500/30 bg-emerald-500/[0.06]' : 'border-amber-500/30 bg-amber-500/[0.07]')}
    >
      {ok ? <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" /> : <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" />}
      <div className="min-w-0 space-y-1">
        <p className="font-semibold">{ok ? 'Passport read successfully' : 'Please check the highlighted details'}</p>
        <p className="text-muted-foreground">
          {ok ? 'Still compare every field with the passport before you continue.' : 'Some values may be misread or missing. Compare every field with the passport.'}
        </p>
        {!extraction.check_digits_valid && <p className="font-medium text-amber-700 dark:text-amber-400">A check digit failed, so at least one value was probably misread.</p>}
        {extraction.warnings.length > 0 && (
          <ul className="list-inside list-disc text-muted-foreground">
            {extraction.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Check page: applicant card with edit + photo
// ---------------------------------------------------------------------------

function Detail({ label, value, arabic }: { label: string; value: string | null | undefined; arabic?: boolean }) {
  return (
    <div className="border-b border-dashed pb-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('mt-0.5 font-semibold', arabic && 'font-arabic text-lg', !value && 'font-normal text-muted-foreground')} dir={arabic ? 'rtl' : undefined}>
        {value || '—'}
      </dd>
    </div>
  );
}

function EditApplicantDialog({ check, open, onOpenChange }: { check: Check; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = React.useState<ApplicantForm>(() => applicantToForm(check.applicant));
  const [errors, setErrors] = React.useState<ApplicantErrors>({});
  React.useEffect(() => {
    if (open) {
      setForm(applicantToForm(check.applicant));
      setErrors({});
    }
  }, [open, check.applicant]);

  const save = useMutation({
    mutationFn: () => checksApi.updateApplicant(check.id, toApplicantPatch(form, check.applicant)),
    onSuccess: (c) => {
      qc.setQueryData(['check', check.id], c);
      qc.invalidateQueries({ queryKey: ['checks'] });
      toast.success('Applicant details saved');
      onOpenChange(false);
    },
    onError: (e) => {
      if (isApiError(e, 422)) setErrors(e.fieldErrors as ApplicantErrors);
      toast.error(userMessage(e));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Applicant details</DialogTitle>
          <DialogDescription>These are printed on the status report. Editing them is free.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            const local = validateApplicant(form);
            if (!form.full_name.trim()) local.full_name = 'The full name is required.';
            setErrors(local);
            if (Object.keys(local).length) return;
            if (Object.keys(toApplicantPatch(form, check.applicant)).length === 0) return onOpenChange(false);
            save.mutate();
          }}
        >
          <ApplicantFields value={form} onChange={setForm} errors={errors} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" loading={save.isPending}>Save details</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ApplicantCard({ check }: { check: Check }) {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState(false);
  const [photoVersion, setPhotoVersion] = React.useState(0);
  const photo = useAuthedImage(check.photo_url, photoVersion);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const a = check.applicant;
  // Same title the report prints (older checks fall back to the default for the sex)
  const shown = applicantToForm(a);

  const onCheck = (c: Check) => {
    qc.setQueryData(['check', check.id], c);
    setPhotoVersion((v) => v + 1);
  };
  const upload = useMutation({
    mutationFn: (f: File) => checksApi.uploadPhoto(check.id, f),
    onSuccess: (c) => {
      onCheck(c);
      toast.success('Photo saved');
    },
    onError: (e) => toast.error(userMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => checksApi.deletePhoto(check.id),
    onSuccess: () => {
      onCheck({ ...check, photo_url: null });
      toast.success('Photo removed');
    },
    onError: (e) => toast.error(userMessage(e)),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Applicant</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          <Pencil /> {a ? 'Edit' : 'Add details'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-4">
          <PhotoFrame src={photo.url} loading={photo.loading || upload.isPending} />
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
                <Camera /> {check.photo_url ? 'Replace' : 'Add photo'}
              </Button>
              {check.photo_url && (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
                  {!remove.isPending && <Trash2 />} Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Printed on the status report.</p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={PHOTO_RULES.accept.join(',')}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              const err = checkFile(f, PHOTO_RULES);
              if (err) toast.error(err);
              else upload.mutate(f);
            }}
          />
        </div>
        {a ? (
          <dl className="grid gap-3 text-sm">
            <Detail label="Full name" value={a.full_name && titledName(a.full_name, shown.title)} />
            <Detail label="Full name (Arabic)" value={a.full_name_ar && titledNameAr(a.full_name_ar, shown.title_ar)} arabic />
            <div className="grid grid-cols-2 gap-3">
              <Detail label="Date of birth" value={a.date_of_birth} />
              <Detail label="Sex" value={a.sex ? SEX_LABEL[a.sex] : null} />
            </div>
            <Detail label="Place of birth" value={a.place_of_birth} />
            <Detail label="Place of birth (Arabic)" value={a.place_of_birth_ar} arabic />
          </dl>
        ) : (
          <p className="rounded-lg bg-secondary/60 p-3 text-sm text-muted-foreground">
            No applicant details yet. Add the name, date and place of birth so they appear on the status report.
          </p>
        )}
      </CardContent>
      <EditApplicantDialog check={check} open={editing} onOpenChange={setEditing} />
    </Card>
  );
}
